import * as THREE from "three";
import type { EffectComposer } from "postprocessing";
import type { TransactionEvent } from "../data/types";
import { ArcLayer } from "./arcs";
import { NodeLayer } from "./nodes";
import { GlobeCamera } from "./camera";
import { buildComposer } from "./postFx";
import { buildGeography } from "./geography";
import { colorNum } from "../theme/tokens";
import { CITIES } from "../data/corridors";

if (import.meta.hot) import.meta.hot.invalidate();

/**
 * Scene root + render loop owner — flat-map edition.
 *
 * Composition: orthographic camera looking nearly top-down at a plane of
 * projected continents, with arcs rising out of the plane in +Z.
 */
export class Globe {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private cam: GlobeCamera;
  private composer!: EffectComposer;
  private running = false;
  private lastFrame = performance.now();

  readonly arcs: ArcLayer;
  readonly nodes: NodeLayer;

  /** Fired when an arc completes its flight. Overlay uses this for amount labels. */
  public onLanding?: (ev: TransactionEvent) => void;

  private readonly _projV = new THREE.Vector3();

  constructor(private readonly container: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: "high-performance",
      stencil: false,
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setClearColor(colorNum.bgDeep, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(colorNum.bgDeep);

    this.cam = new GlobeCamera(container.clientWidth / container.clientHeight);
    this.scene.add(this.cam.root);

    this.arcs = new ArcLayer();
    this.nodes = new NodeLayer();
    this.cam.root.add(this.arcs.object);
    this.cam.root.add(this.nodes.object);

    this.composer = buildComposer(this.renderer, this.scene, this.cam.camera);

    window.addEventListener("resize", this.onResize);
  }

  async init() {
    const geo = await buildGeography();
    this.cam.root.add(geo);
  }

  dispatch(ev: TransactionEvent, now: number) {
    const destPulseColor =
      ev.product === "card" ? colorNum.productCard : colorNum.productSend;
    this.arcs.dispatch(ev, now, (completed) => {
      this.nodes.pulseAt(
        completed.dest.lat,
        completed.dest.lng,
        performance.now(),
        destPulseColor,
      );
      this.onLanding?.(completed);
    });
  }

  /**
   * Update per-city glow brightness from normalized transaction activity.
   * activities: Map<cityIndex, 0..1> — missing entries keep their last value.
   */
  updateCityActivity(activities: Map<number, number>) {
    const arr = new Float32Array(CITIES.length);
    // Pre-fill with existing attribute values so missing entries don't reset.
    for (let i = 0; i < CITIES.length; i++) {
      const v = activities.get(i);
      arr[i] = v !== undefined ? v : 0.5;
    }
    this.nodes.setActivityBatch(arr);
  }

  /**
   * Fire a non-arc activity ring at a city — used for deposits and
   * conversions (wallet operations that don't cross borders).
   */
  pulseActivity(lat: number, lng: number, colorHex: number) {
    this.nodes.pulseAt(lat, lng, performance.now(), colorHex);
  }

  /** Project world-plane coords (x, y) to screen pixels. */
  projectToScreen(worldX: number, worldY: number): { x: number; y: number } {
    this._projV.set(worldX, worldY, 0.02);
    this._projV.project(this.cam.camera);
    const canvas = this.renderer.domElement;
    return {
      x: (this._projV.x * 0.5 + 0.5) * canvas.clientWidth,
      y: (1 - (this._projV.y * 0.5 + 0.5)) * canvas.clientHeight,
    };
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastFrame = performance.now();
    this.loop();
  }

  stop() {
    this.running = false;
  }

  get camera() {
    return this.cam;
  }

  private loop = () => {
    if (!this.running) return;
    const now = performance.now();
    const dt = now - this.lastFrame;
    this.lastFrame = now;

    this.cam.update(dt);
    this.arcs.update(now);
    this.nodes.update(now);
    this.composer.render(dt / 1000);

    requestAnimationFrame(this.loop);
  };

  private onResize = () => {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setSize(w, h);
    this.cam.setAspect(w / h);
    this.composer.setSize(w, h);
  };

  dispose() {
    this.running = false;
    window.removeEventListener("resize", this.onResize);
    this.renderer.dispose();
    this.composer.dispose();
    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
  }
}
